import React, { useEffect, memo, useRef, useCallback } from 'react'
import {
    ValueFormatterParams,
    GetContextMenuItemsParams,
    ICellRendererParams,
    RowHeightParams,
} from 'ag-grid-community'
import {
    CreditCardProcessorType,
    VerdictType,
    AGDataGrid,
    CaseHistory,
} from 'components'
import { useColumnDefsAGGrid } from 'hooks/useColumnDefs/useColumnDefs'
import { ExtendedColumn } from 'components/DataGrid/ExtendedColumn'
import {
    ActionsCell,
    ViewDocumentIconAction,
    EditChargebackCell,
    IssuerDocsCell,
} from './Cells'
import { DueDateCell } from 'views/CaseManagementAGGrid/grids/Cells'
import { CaseData } from 'hooks/cb-api'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import { alertSnackbarContentProps } from 'components/AlertSnackbar'
import { CLIENT_THEME_VARIANT } from 'hooks/useThemeStyling/useThemeStyling'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'

interface ChargebacksGridProps {
    canViewPartnerCompanyId: boolean
    canViewIssuerDocs: boolean
    canViewOutcomeVerdicts: boolean
    canViewPlatform: boolean
    gridRef: any
    data: CaseData[]
    dataSource: any
    isLoading: boolean
    pageSize: number
    page: number
    paginationTotal: number
    dataGridColumns: ExtendedColumn[]
    setDataGridColumns: (columns: ExtendedColumn[]) => void
    setLocalPage: (page: number) => void
    handlePageLimitChange: (pageSize: number) => void
    handleSearchField: (value: string) => void
    currentPayload: { [key: string]: any } | undefined
    refreshChargebacksGrid: () => void
    getContextMenuItems: (params: GetContextMenuItemsParams) => void
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
    isLoadingAdvFilters: boolean
    showClientTypeIssuerUI: boolean
}

export const amountValueFormatter = (value: string) => {
    return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

export const ChargebacksGrid = ({
    canViewPartnerCompanyId,
    canViewIssuerDocs,
    canViewOutcomeVerdicts,
    canViewPlatform,
    gridRef,
    // data,
    dataSource,
    isLoading,
    pageSize,
    page,
    paginationTotal,
    dataGridColumns,
    setDataGridColumns,
    setLocalPage,
    handlePageLimitChange,
    // handleSearchField,
    // currentPayload,
    refreshChargebacksGrid,
    getContextMenuItems,
    setAlertSnackbarMainProps,
    setAlertSnackbarMainOpen,
    isLoadingAdvFilters,
    showClientTypeIssuerUI,
}: ChargebacksGridProps) => {
    const { user } = useAuthedUser()
    const { themeVariantObj } = useThemeStyling()
    const { DATA_GRID } = useFeatureToggle('CHARGEBACKS')
    const showContextMenu =
        DATA_GRID.CONTEXT_MENU?.enabled && !showClientTypeIssuerUI

    const tableOuterContainerEL = useRef<HTMLDivElement>(null)

    const { colDefs: columns } = useColumnDefsAGGrid<CaseData>(
        'chargebacks',
        //@ts-ignore
        user?.role?.alias,
        {
            cells: {
                actions: memo((params: ValueFormatterParams) => (
                    <ActionsCell
                        rowData={params}
                        rehydrateView={refreshChargebacksGrid}
                        testId={'chargebacks'}
                        setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                        setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                        showClientTypeIssuerUI={showClientTypeIssuerUI}
                    />
                )),
                document: memo((params: ICellRendererParams) => (
                    <ViewDocumentIconAction
                        caseId={params.data.id}
                        disabled={!params.data?.properties?.is_doc_available}
                        rowIndex={params.rowIndex}
                        testId={'chargebacks'}
                        setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                        setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    />
                )),
                cc_type: memo((params: ValueFormatterParams) => (
                    <CreditCardProcessorType
                        processor={params.data?.cc_type}
                        size="medium"
                        className={'emp-cardTypeIcon'}
                    />
                )),
                verdict: memo((params: ValueFormatterParams) => (
                    <VerdictType
                        verdict={params.data?.verdict}
                        size="small"
                        className={'emp-verdictIcon'}
                    />
                )),
                dispute_amt: memo((params: ValueFormatterParams) => (
                    <span style={{ textAlign: 'right', display: 'block' }}>
                        {amountValueFormatter(params.data?.dispute_amt)}
                    </span>
                )),
                trans_amt: memo((params: ValueFormatterParams) => (
                    <span style={{ textAlign: 'right', display: 'block' }}>
                        {amountValueFormatter(params.data?.trans_amt)}
                    </span>
                )),
                edit: memo((params: ICellRendererParams) => (
                    <EditChargebackCell
                        rowData={params.data}
                        refreshGrid={refreshChargebacksGrid}
                        rowIndex={params.rowIndex}
                        setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                        setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    />
                )),
                issuer_doc: memo((params: ICellRendererParams) => (
                    <IssuerDocsCell
                        issuerDocs={params.data?.issuer_docs ?? []}
                        caseId={params.data?.id}
                        rowIndex={params.rowIndex}
                        testId={'chargebacks'}
                        setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                        setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    />
                )),
                case_menu: memo((params: ICellRendererParams) => (
                    <CaseHistory
                        row={params.data}
                        rowIndex={params.rowIndex}
                        testId={'chargebacks'}
                        className={'emp-caseMenuIcon'}
                    />
                )),
                is_credit: memo((params: ValueFormatterParams) => (
                    <span>{params.data.is_credit ? 'Yes' : 'No'}</span>
                )),
                is_guaranteed: memo((params: ValueFormatterParams) => (
                    <span>{params.data.is_guaranteed ? 'Yes' : 'No'}</span>
                )),
                due_date: memo((params: ICellRendererParams) => (
                    <DueDateCell row={params.data} />
                )),
                creation_date: memo((params: ICellRendererParams) => (
                    <div
                        id={'caseMgmtAGDueDateCell'}
                        style={{ textAlign: 'center' }}
                    >
                        {params.data.creation_date}
                    </div>
                )),
                trans_date: memo((params: ICellRendererParams) => (
                    <div
                        id={'caseMgmtAGTransDateCell'}
                        style={{ textAlign: 'center' }}
                    >
                        {params.data.trans_date}
                    </div>
                )),
            },
        }
    )

    useEffect(() => {
        if (columns?.length) {
            const filteredColumns = columns
                .filter((i) => {
                    if (i.accessor === 'partner_company_id') {
                        return canViewPartnerCompanyId
                    }
                    if (i.accessor === 'issuer_doc') {
                        return canViewIssuerDocs
                    }
                    if (i.accessor === 'verdict') {
                        return canViewOutcomeVerdicts || showClientTypeIssuerUI
                    }
                    if (i.accessor === 'processor') {
                        return canViewPlatform
                    }
                    if (i.accessor === 'merchant_alias') {
                        return showClientTypeIssuerUI
                    }
                    if (i.accessor === 'actions') {
                        return !showClientTypeIssuerUI
                    }
                    if (i.accessor === 'edit') {
                        return ![CLIENT_THEME_VARIANT.BARCLAYS.id].includes(
                            themeVariantObj.id
                        )
                    }
                    return true
                })
            setDataGridColumns(filteredColumns)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        columns,
        canViewPartnerCompanyId,
        canViewIssuerDocs,
        canViewOutcomeVerdicts,
        canViewPlatform,
    ])

    const defaultColDef = {
        flex: 1,
        minWidth: 120,
        resizable: true,
    }

    const handleMasterDetailRowHeight = useCallback(
        (params: RowHeightParams) => {
            if (params.node && params.node?.detail) {
                const detailDataLength = params.data?.details_data?.length ?? 0
                if (!detailDataLength) return 90
                return 35 * detailDataLength + 105
            }
        },
        []
    )

    return (
        <div id={'chargebacksGridAG'}>
            <div
                ref={tableOuterContainerEL}
                className={'emp-dataGridContainer'}
            >
                <AGDataGrid
                    gridRef={gridRef}
                    gridName={'chargebacks'}
                    fileName={'chargebacks'}
                    columns={dataGridColumns}
                    dataSource={dataSource}
                    loading={isLoading || isLoadingAdvFilters}
                    testId={'chargebacks'}
                    onPageLimitChange={handlePageLimitChange}
                    pageSize={pageSize}
                    page={page}
                    onPageChange={setLocalPage}
                    totalEntries={paginationTotal}
                    defaultColDef={defaultColDef}
                    getCustomContextMenuItems={
                        showContextMenu ? getContextMenuItems : undefined
                    }
                    enableSelectAllCheckbox={showContextMenu}
                    enableSelectOneCheckbox={showContextMenu}
                    rowSelectionType={'multiple'}
                    disableRowClickSelection
                    fullHeightContainerRef={tableOuterContainerEL}
                    refreshDataGrid={refreshChargebacksGrid}
                    rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]}
                    gridThemeType={'materialSlim'}
                    className={'emp-grid'}
                    masterDetailType={
                        showClientTypeIssuerUI
                            ? 'chargebacksDetails'
                            : undefined
                    }
                    masterDetailRowHeight={185}
                    handleMasterDetailRowHeight={handleMasterDetailRowHeight}
                />
            </div>
        </div>
    )
}
